<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
  import { useDraftActions } from '@/composition/menu-import/use-draft-actions';
  import { othersCardGroups } from '@/domain/menu-import/live-update';
  import type { ILiveOther, IMenuDraft } from '@/interfaces/IMenuImport';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2MenuOthersRow from './V2MenuOthersRow.vue';

  /**
   * «{count} platos de su menú no están en las fotos» (plan §3.5 «Others
   * card»), after the last category of an update: they stay as they are;
   * the owner hides only the ones no longer sold. Folded by default; open,
   * the dishes missing from each photographed category come first («Ocultar
   * los {count}» per group), then the rest. A hide waits for the server (the
   * tapped button is busy meanwhile). The focus follows what opens, and a
   * button that goes away hands it to its group's title.
   */
  const props = defineProps<{
    draft: IMenuDraft;
    /** `.sync`: the workspace opens it from the summary link. */
    expanded: boolean;
    currency: string;
    country: string;
  }>();
  const emit = defineEmits<{
    (e: 'update:expanded', expanded: boolean): void;
  }>();
  const actions = useDraftActions({ country: () => props.country });

  /** Estimated height of a dish row, for `contain-intrinsic-size`. */
  const ROW_ESTIMATE_PX = 52;
  /** The group of the dishes left out of every photographed category. */
  const OTHER_GROUP = 'other';
  const uid = nextDomId('menu-others');
  const titleId = `${uid}-title`;

  const root = ref<HTMLElement | null>(null);
  const otherOpen = ref(false);
  /** Buttons waiting for their change to come back ('group-…' for «Ocultar los {count}»). */
  const busy = ref<ReadonlySet<string>>(new Set());
  let alive = true;

  const groups = computed(() => othersCardGroups(props.draft));
  /** «No estaban en las fotos» folds only below the photographed groups. */
  const otherShown = computed(
    () => otherOpen.value || groups.value.photographed.length === 0
  );
  const hiddenIds = computed(
    () => new Set(props.draft.live ? props.draft.live.hiddenIds : [])
  );

  function nameId(other: ILiveOther): string {
    return `${uid}-dish-${other.itemId}`;
  }

  function groupTitleId(key: string): string {
    return `${uid}-group-${key}`;
  }

  function lazyStyle(count: number) {
    return { '--rows-height': `${Math.max(count, 1) * ROW_ESTIMATE_PX}px` };
  }

  /** The group's dishes not hidden yet («Ocultar los {count}» from 2). */
  function visibleIds(dishes: ILiveOther[]): number[] {
    return dishes
      .filter((other) => !hiddenIds.value.has(other.itemId))
      .map((other) => other.itemId);
  }

  /** Moves the focus to a heading of the card (`tabindex="-1"`), once rendered. */
  async function focusById(id: string) {
    await nextTick();
    if (!alive) return;
    const heading = document.getElementById(id);
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
  }

  function isFocusInside(): boolean {
    const active = document.activeElement;
    return active instanceof Node && !!root.value?.contains(active);
  }

  async function run(key: string, liveItemIds: number[], hidden: boolean) {
    if (busy.value.has(key) || liveItemIds.length === 0) return;
    busy.value = new Set([...busy.value, key]);
    await actions.hideOthers(liveItemIds, hidden);
    if (!alive) return;
    busy.value = new Set([...busy.value].filter((entry) => entry !== key));
  }

  function toggleDish(other: ILiveOther) {
    void run(
      `dish-${other.itemId}`,
      [other.itemId],
      !hiddenIds.value.has(other.itemId)
    );
  }

  async function hideGroup(key: string, dishes: ILiveOther[]) {
    await run(`group-${key}`, visibleIds(dishes), true);
    // Its button is gone once fewer than 2 are left: the focus goes to the group.
    if (alive && !isFocusInside()) void focusById(groupTitleId(key));
  }

  function expand() {
    emit('update:expanded', true);
    void focusById(
      groups.value.photographed.length > 0
        ? groupTitleId(String(groups.value.photographed[0].category.id))
        : groupTitleId(OTHER_GROUP)
    );
  }

  function openOther() {
    otherOpen.value = true;
    void focusById(groupTitleId(OTHER_GROUP));
  }

  /** The summary link brought the card into view: keyboard and screen readers land on it. */
  function focusTitle() {
    void focusById(titleId);
  }

  defineExpose({ focusTitle });

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <section
    v-if="groups.total > 0"
    ref="root"
    class="v2-menu-others-card"
    :aria-labelledby="titleId"
  >
    <header class="card-head">
      <h2 :id="titleId" class="card-title" tabindex="-1">
        {{
          $tc('menuImport.update.othersTitle', groups.total, {
            count: groups.total,
          })
        }}
      </h2>
      <p class="card-text">{{ $t('menuImport.update.othersText') }}</p>
      <button
        v-if="!expanded"
        type="button"
        class="card-link"
        aria-expanded="false"
        @click="expand"
      >
        {{
          $tc('menuImport.update.othersShow', groups.total, {
            count: groups.total,
          })
        }}
      </button>
    </header>
    <div v-if="expanded" class="card-body">
      <section
        v-for="group in groups.photographed"
        :key="group.category.id"
        class="others-group"
      >
        <div class="group-head">
          <h3
            :id="groupTitleId(String(group.category.id))"
            class="group-title"
            tabindex="-1"
          >
            {{
              $t('menuImport.update.groupPhotographed', {
                category: group.category.name,
              })
            }}
          </h3>
          <button
            v-if="visibleIds(group.dishes).length >= 2"
            type="button"
            class="card-link"
            :disabled="busy.has(`group-${group.category.id}`)"
            @click="hideGroup(String(group.category.id), group.dishes)"
          >
            {{
              $t('menuImport.update.hideGroup', {
                count: visibleIds(group.dishes).length,
              })
            }}
          </button>
        </div>
        <ul class="group-rows" :style="lazyStyle(group.dishes.length)">
          <V2MenuOthersRow
            v-for="other in group.dishes"
            :key="other.itemId"
            :other="other"
            :hiding="hiddenIds.has(other.itemId)"
            :busy="busy.has(`dish-${other.itemId}`)"
            :name-id="nameId(other)"
            :currency="currency"
            :country="country"
            @toggle="toggleDish(other)"
          />
        </ul>
      </section>
      <section v-if="groups.other.length > 0" class="others-group">
        <div class="group-head">
          <h3 :id="groupTitleId(OTHER_GROUP)" class="group-title" tabindex="-1">
            {{ $t('menuImport.update.groupOther') }}
          </h3>
          <button
            v-if="!otherShown"
            type="button"
            class="card-link"
            aria-expanded="false"
            @click="openOther"
          >
            {{
              $tc('menuImport.update.othersShow', groups.other.length, {
                count: groups.other.length,
              })
            }}
          </button>
        </div>
        <ul
          v-if="otherShown"
          class="group-rows"
          :style="lazyStyle(groups.other.length)"
        >
          <V2MenuOthersRow
            v-for="other in groups.other"
            :key="other.itemId"
            :other="other"
            :hiding="hiddenIds.has(other.itemId)"
            :busy="busy.has(`dish-${other.itemId}`)"
            :name-id="nameId(other)"
            :currency="currency"
            :country="country"
            @toggle="toggleDish(other)"
          />
        </ul>
      </section>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-others-card {
    @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;

    .card-head {
      @apply tw-flex tw-flex-col tw-items-start tw-gap-1;
    }
    .card-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
      &:focus {
        @apply tw-outline-none;
      }
    }
    .card-text {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500;
    }
    .card-link {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .card-body {
      @apply tw-flex tw-flex-col tw-gap-4 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-3;
    }
    .others-group {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .group-head {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3;
    }
    .group-title {
      @apply tw-m-0 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      &:focus {
        @apply tw-outline-none;
      }
    }
    // Rows far off screen skip layout and paint; the padding keeps the
    // buttons' focus rings inside the box it clips.
    .group-rows {
      @apply -tw-m-1 tw-flex tw-list-none tw-flex-col tw-p-1;
      content-visibility: auto;
      contain-intrinsic-size: var(--rows-height);
      contain-intrinsic-size: auto var(--rows-height);
    }
  }
</style>
